<script lang="ts" setup>
  import { ref, onMounted } from 'vue';
  import { useCart } from '@storefront/storefront';
  import { useHttpClient, useConfirmation, useRoute, useTranslator } from '@othercommerce/foundation';
  import { DiscountMode } from '@othercommerce/domain';

  interface CustomerLoyaltyProgramDetails {
    isActive: Boolean,
    minPointsForOrder: number,
    totalPoints: number,
    value: string,
  }

  const emit = defineEmits<{
    (e: 'toggle', section: string): void,
  }>();

  defineProps({
    open: { type: Boolean, default: false },
  });

  const { cart } = useCart();
  const route = useRoute();
  const http = useHttpClient();
  const confirm = useConfirmation();
  const translator = useTranslator();
  const loyalty = ref<CustomerLoyaltyProgramDetails>();

  onMounted(() => {
    http.get(route('web.cart.loyalty.show')).then((res) => {
      loyalty.value = res;
    });
  });

  function onSubmit(data: number | null) {
    return http.patch(route('web.cart.loyalty.update'), data);
  }

  function onDestroy() {
    confirm({
      callback: () => http.delete(route('web.cart.loyalty.destroy')),
      title: translator.trans('SHOP.CART.HEADER.LOYALTY_DISCOUNT_REMOVE'),
      description: translator.trans('SHOP.CART.MESSAGE.LOYALTY_DISCOUNT_REMOVE'),
      yes: translator.trans('SHOP.ACTION.DESTROY'),
      no: translator.trans('SHOP.ACTION.CANCEL'),
    }).catch(() => void (0));
  }
</script>
<template>
  <article v-if="cart" class="order-loyalty-points">
    <template v-if="cart.loyaltyPoints === 0">
      <FormController v-slot="{ processing, submit }" aria-labelledby="order-loyalty-points__heading" class="order-loyalty-points__form" @submit="onSubmit">
        <div
          :aria-pressed="open"
          aria-controls="order-loyalty-points__apply"
          class="order-loyalty-points__header"
          role="button"
          tabindex="0"
          @click="$emit('toggle', 'loyalty')"
          @keydown.space="$emit('toggle', 'loyalty')"
        >
          <h3 id="order-loyalty-points__heading" class="order-loyalty-points__heading">
            {{ $t('SHOP.CART.HEADER.USE_LOYALTY_POINTS') }}
          </h3>
          <SfIconPlus :class="{ 'order-loyalty-points__icon--open': open }" aria-hidden="true" class="order-loyalty-points__icon order-loyalty-points__icon--apply" />
        </div>
        <div v-if="loyalty" id="order-loyalty-points__apply" :class="{ 'order-loyalty-points__content--open': open }" :inert="!open" class="order-loyalty-points__content">
          <div class="order-loyalty-points__content-outer">
            <div class="order-loyalty-points__total">
              <p class="order-loyalty-points__remaining">
                <span class="order-loyalty-points__total-label">
                  {{ $t('SHOP.CART.LABEL.LOYALTY_POINTS_AVAILABLE') }}
                </span>
                <span class="order-loyalty-points__total-value">
                  {{ loyalty.totalPoints }} {{ $t('SHOP.CART.LABEL.POINTS_SHORT') }}
                </span>
              </p>
              <p class="order-loyalty-points__value">
                <span class="order-loyalty-points__value-label">
                  {{ $t('SHOP.CART.LABEL.LOYALTY_POINTS_VALUE') }}
                </span>
                <span class="order-loyalty-points__value-wrapper">
                  <SfMoney :code="cart.currency.code" :value="loyalty.value" class="order-loyalty-points__money" />
                </span>
              </p>
            </div>
            <div class="order-loyalty-points__content-inner">
              <SfFormNumber :floating="false" :label="$t('SHOP.CART.LABEL.LOYALTY_POINTS')" :min="loyalty.minPointsForOrder" class="order-loyalty-points__input" name="points" step="1" />
              <SfButton :loading="processing" class="order-loyalty-points__add-button" primary type="submit" @click="submit">
                {{ $t('SHOP.COMMON.ACTION.ADD') }}
              </SfButton>
            </div>
          </div>
        </div>
      </FormController>
    </template>
    <template v-else>
      <article id="order-loyalty-points__applied">
        <header class="order-loyalty-points__applied-header">
          <h3 class="order-loyalty-points__applied-heading">{{ $t('SHOP.CART.HEADER.LOYALTY_POINTS_APPLIED') }}</h3>
          <p class="order-loyalty-points__applied-value">{{ cart.loyaltyPoints }}</p>
        </header>
        <section class="order-loyalty-points__applied-section">
          <SfDiscount :type="DiscountMode.FIXED" :value="cart.loyaltyValue" class="order-loyalty-points__discount" />
          <SfButton class="order-loyalty-points__delete" danger small transparent @click="onDestroy">
            <template #before>
              <SfIconTrash aria-hidden="true" class="order-loyalty-points__icon order-loyalty-points__icon--delete" />
            </template>
            <template #default>
              {{ $t('SHOP.CART.ACTION.REMOVE_LOYALTY_POINTS') }}
            </template>
          </SfButton>
        </section>
      </article>
    </template>
  </article>
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  :root {
    --sf-order-loyalty-transition-timing-func: ease-in-out;
    --sf-order-loyalty-transition-duration: 200ms;

    --sf-order-loyalty-border-width: 1px;
    --sf-order-loyalty-border-style: solid;
    --sf-order-loyalty-border-color: var(--secondary-color);
  }

  .order-loyalty-points {
    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 0.875rem;
      cursor: pointer;
      text-decoration: underline;
      text-decoration-color: transparent;
      transition: text-decoration-color 0.2s ease-out;


      @media(hover: hover) {
        &:hover {
          text-decoration-color: var(--primary-color);
        }
      }
    }

    &__heading {
      font-size: 0.875rem;
      line-height: 1.25rem;
      font-weight: var(--font-weight-regular);
    }

    &__icon {
      &--apply {
        transform: rotate(0deg);
        transform-origin: 50% 50%;
        font-size: 1.25rem;
        line-height: 1;
        transition: transform;
        transition-duration: var(--sf-order-loyalty-transition-duration);
        transition-timing-function: var(--sf-order-loyalty-transition-timing-func);
      }

      &--open {
        transform: rotate(45deg);
      }
    }

    &__content {
      display: grid;
      grid-template-rows: 0fr;
      transition: grid-template-rows;
      transition-duration: var(--sf-order-loyalty-transition-duration);
      transition-timing-function: var(--sf-order-loyalty-transition-timing-func);

      &--open {
        grid-template-rows: 1fr;
      }
    }

    &__total {
      margin: 0.625rem 0;
      font-size: 0.875rem;
      line-height: 1.5;
    }

    &__total-value {
      font-weight: var(--font-weight-semi);
    }

    &__content-outer {
      overflow: hidden;
    }

    &__content--open .order-loyalty-points__content-outer {
      overflow: visible;
    }

    &__content-inner {
      display: flex;
      align-items: flex-end;
      gap: 1rem;
      padding: 1rem 0.375rem;
      border-bottom-color: var(--sf-order-coupon-border-color);
      border-bottom-style: var(--sf-order-coupon-border-style);
      border-bottom-width: var(--sf-order-coupon-border-width);

      .control__wrapper--number,
      .btn {
        flex-basis: 50%;
      }
    }

    &__input {
      display: inline-flex;
      flex-grow: 0;
    }

    #order-loyalty-points__applied {
      font-size: 0.875rem;
      padding-bottom: 1rem;
      border-bottom-color: var(--sf-order-coupon-border-color);
      border-bottom-style: var(--sf-order-coupon-border-style);
      border-bottom-width: var(--sf-order-coupon-border-width);
    }

    &__applied-header {
      font-weight: var(--font-weight-semi);
      margin-bottom: 0.375rem;
    }

    &__applied-heading {
      font-size: 0.875rem;
      font-weight: var(--font-weight-regular);
    }

    &__applied-section {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
    }
  }

  @include media.query(lg) {
    .order-loyalty-points {
      margin-top: 0.625rem;

      &__content {
        margin-top: 0.375rem;
      }
    }
  }
</style>
